import { useEffect } from "react";
import { AuthenticatedTemplate, MsalProvider, UnauthenticatedTemplate, useMsal } from "@azure/msal-react";
import { EventType, type AuthenticationResult } from "@azure/msal-browser";

import Typography from "@mui/material/Typography";

import Box from "ui/atom/Box";
import Button from "ui/base/Button";

import { useLayoutMsal } from "features/layout/fabric";
import { useSettings } from "features/start/fabric";

import client from "api/client";


Msal.Body = ({ children }: { children: React.ReactNode }) => {
	const { instance, accounts } = useMsal();
	const active = accounts.length > 0;

	const handleLogin = () => instance.loginPopup({ prompt: "select_account", scopes: [] });

	const style = {
		root: {
			alignItems: "center",
			justifyContent: "center",
			maxWidth: "sm",
			margin: "auto",
			height: "100vh"
		}
	};

	if (active) return (
		<AuthenticatedTemplate>
			{children}
		</AuthenticatedTemplate>
	);

	return (
		<UnauthenticatedTemplate>
			<Box.Column gap={2} sx={style.root} >
				<Typography variant="h2">You are not signed in</Typography>
				<Button variant="contained" color="secondary" onClick={handleLogin}>Sign in</Button>
			</Box.Column>
		</UnauthenticatedTemplate>
	);
};

export default function Msal({ children }: { children: React.ReactNode }) {
	const layout = useLayoutMsal();
	const settings = useSettings();

	useEffect(() => {
		if (!layout.msal || !settings?.azureAd) return;

		let callbackId: string | null = null;
		let cancelled = false;

		layout.msal.initialize()
			.then(async () => {
				if (cancelled || !layout.msal) return;
				const account = layout.msal.getActiveAccount();
				if (!account && layout.msal.getAllAccounts().length > 0) layout.msal.setActiveAccount(layout.msal.getAllAccounts()[0]);
				layout.msal.enableAccountStorageEvents();
				callbackId = layout.msal.addEventCallback((event) => {
					const authenticationResult = event?.payload as AuthenticationResult;
					if (event.eventType === EventType.LOGIN_SUCCESS && authenticationResult?.account) {
						const account = authenticationResult?.account;
						layout.msal?.setActiveAccount(account);
						window.location.reload();
					}
				});

				client.setAuthTokenProvider(async () => {
					const active = layout.msal?.getActiveAccount() ?? layout.msal?.getAllAccounts()[0];
					if (!active) return undefined;
					const token = await layout.msal?.acquireTokenSilent({
						account: active,
						authority: `${settings.azureAd.instance}${settings.azureAd.tenantId}/v2.0`,
						scopes: [`${settings.azureAd.audience}/full_access`],
						forceRefresh: true
					});
					return token?.accessToken;
				});
			});

		return () => {
			cancelled = true;
			if (callbackId && layout.msal) layout.msal.removeEventCallback(callbackId);
		};
	}, [layout.msal]);

	if (layout.msal) return (
		<MsalProvider instance={layout.msal}>
			<Msal.Body>
				{children}
			</Msal.Body>
		</MsalProvider>
	);
}